import type { ProcessAccordionHeaderProps } from '@ksyos/design-system/dist/components/process-accordion';
import type { ProcessActivityHeaderBadgeItem } from '@ksyos/design-system/dist/components/process-activity';
import type { ProcessActivityHeaderActionItem } from '@ksyos/design-system/dist/components/process-activity/process-activity.types';
import type { JSX } from 'react';
import {
    Box,
    ProcessAccordionContent,
    ProcessAccordionHeader,
    ProcessAccordionItem,
    ProcessActivityHeader,
    ProcessActivityItem,
} from '@ksyos/design-system';
import { useMemo, useState } from 'react';
import type { IProcessActivity } from '@common/interfaces';
import type { IProcessActivityReadSection } from '@common/interfaces/processActivityReadSection';
import { CustomControlMode, ProcessActivityStatus, ServiceActivityType, ToastType } from '@common/enums';
import { addTextBlockWithEachItemOnOneLine } from '@common/utils/addTextBlock';
import { formatString } from '@common/utils/formatString';
import { getRegistryEntry } from '@common/workflow/serviceActivityRegistration';
import { PatientEpisodeCategoryCarePathContext } from '@frontend/components/pages/episodeCategory/carePath/patientEpisodeCategoryCarePathContext';
import { findRegistryEntry } from '@frontend/components/pages/episodeCategory/customControls/customControl';
import { ReadForm } from '@frontend/formEngine/readForm/ReadForm';
import { downloadPdf } from '@frontend/services/api/processActivitiesApiService';
import { getSelectedLanguage } from '@frontend/translate';
import { showToast } from '@frontend/utils';
import { useDefinedContext } from '@frontend/utils/hooks/useDefinedContext';
import { ProcessActivityContext } from '../processActivityContext';
import { getCommonHeaderProps } from '../processActivityUtil';
import { ModificationDetails } from './ModificationDetails';
import { ProcessActivityCancelModal } from './ProcessActivityCancelModal';
import { ProcessActivityReadActionButtonRow } from './ProcessActivityReadActionButtonRow';
import { ProcessActivityReadContextProvider } from './ProcessActivityReadContextProvider';
import { ProcessActivityRemoveModal } from './ProcessActivityRemoveModal';

type ProcessActivityReadProps = Readonly<{
    processActivity: IProcessActivity;
}>;

export function ProcessActivityRead(props: ProcessActivityReadProps): JSX.Element | null {
    const { processActivity } = props;
    const { serviceActivityType } = processActivity.serviceActivity;
    const {
        activeModifyActivity,
        activeProcessAction,
        activeStartingPoint,
        deleteProcessActionDraft,
        setActiveProcessAction,
        setActiveModifyActivity,
    } = useDefinedContext(PatientEpisodeCategoryCarePathContext);
    const { processAction } = useDefinedContext(ProcessActivityContext);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);

    const registryEntry = findRegistryEntry(
        processActivity.serviceActivity.customControlCode,
        processActivity.customControlVersion
    );
    const commonRegistryEntry = getRegistryEntry(
        processActivity.serviceActivity.customControlCode,
        processActivity.customControlVersion
    );
    const readSections: IProcessActivityReadSection[] = useMemo(transformReadSections, [
        registryEntry,
        processActivity.readSections,
    ]);
    const hasElementToDisplay =
        Boolean(registryEntry?.ReadElement) ||
        Boolean(commonRegistryEntry.sections) ||
        Boolean(processActivity.processActivityModification);

    return (
        <ProcessActivityReadContextProvider
            processActivity={processActivity}
            readSections={readSections}
        >
            {!hasElementToDisplay && (
                <ProcessActivityItem mode={CustomControlMode.read}>
                    <Box
                        style={{
                            marginRight: '64px',
                        }}
                    >
                        <ProcessActivityHeader
                            {...getCommonHeaderProps(false, processActivity.serviceActivity, processActivity)}
                            {...getProcessAccordionHeaderActions()}
                            badgeItems={getBadgeItems()}
                            isSensitiveData={true}
                        />
                    </Box>
                </ProcessActivityItem>
            )}
            {hasElementToDisplay && (
                <ProcessAccordionItem value={processActivity.id}>
                    <ProcessAccordionHeader
                        {...getCommonHeaderProps(hasElementToDisplay, processActivity.serviceActivity, processActivity)}
                        {...getProcessAccordionHeaderActions()}
                        badgeItems={getBadgeItems()}
                    />

                    <ProcessAccordionContent>
                        {registryEntry?.ReadElement ? <registryEntry.ReadElement /> : <ReadForm />}
                        {processActivity.processActivityModification && (
                            <ModificationDetails modification={processActivity.processActivityModification} />
                        )}
                        {!registryEntry?.hideReadActions && (
                            <ProcessActivityReadActionButtonRow
                                activityTitle={processActivity.title}
                                canBeCancelled={canBeCancelled()}
                                canBeRemoved={canBeRemoved()}
                                canCopyReadSectionsToClipboard={canCopyReadSectionsToClipboard()}
                                canEdit={canEdit()}
                                canModify={Boolean(processActivity.canBeModified) && !activeProcessAction}
                                canPrint={canPrint()}
                                handleClickCancel={handleClickCancel}
                                handleClickCopyReadSectionsToClipboard={handleClickCopyReadSectionsToClipboard}
                                handleClickEdit={handleClickEdit}
                                handleClickModify={handleClickModify}
                                handleClickPrint={handleClickPrint}
                                onClickRemove={() => setShowRemoveModal(true)}
                            />
                        )}
                    </ProcessAccordionContent>
                </ProcessAccordionItem>
            )}
            {showRemoveModal && (
                <ProcessActivityRemoveModal
                    onCancel={() => setShowRemoveModal(false)}
                    onConfirm={handleConfirmRemove}
                />
            )}
            {showCancelModal && <ProcessActivityCancelModal onClose={() => setShowCancelModal(false)} />}
        </ProcessActivityReadContextProvider>
    );

    function canBeCancelled(): boolean {
        return Boolean(processActivity.canBeCancelled) && !activeProcessAction;
    }

    function canCopyReadSectionsToClipboard(): boolean {
        return processActivity.status === ProcessActivityStatus.complete && Boolean(registryEntry?.canCopyReadSections);
    }

    function canEdit(): boolean {
        return (
            processActivity.status === ProcessActivityStatus.draft &&
            Boolean(processAction) &&
            !processAction?.isPaused &&
            !activeStartingPoint &&
            !activeProcessAction &&
            !activeModifyActivity
        );
    }

    function canBeRemoved(): boolean {
        return Boolean(processActivity.canBeRemoved) && canEdit();
    }

    function canPrint(): boolean {
        return (
            processActivity.status === ProcessActivityStatus.complete && processActivity.serviceActivity.isPrintEnabled
        );
    }

    function getBadgeItems(): ProcessActivityHeaderBadgeItem[] | undefined {
        if (!processActivity || processActivity.badges.length === 0) {
            return;
        }

        return processActivity.badges.map((badge) => ({ ...badge, key: badge.text }));
    }

    function getProcessAccordionHeaderActions(): Pick<
        ProcessAccordionHeaderProps,
        'primaryActionItems' | 'secondaryActionItems'
    > {
        const primaryActionItems: ProcessActivityHeaderActionItem[] = [];
        const secondaryActionItems: ProcessActivityHeaderActionItem[] = [];

        if (canEdit() && !registryEntry?.hideReadActions) {
            primaryActionItems.push({
                key: 'edit',
                label: getSelectedLanguage().common.edit,
                onClick: handleClickEdit,
            });
        }

        if (canBeRemoved()) {
            secondaryActionItems.push({
                key: 'remove',
                label: getSelectedLanguage().common.remove,
                onClick: () => setShowRemoveModal(true),
            });
        }

        if (canBeCancelled()) {
            const cancelPosition = hasElementToDisplay ? secondaryActionItems : primaryActionItems;
            const cancelLabel =
                getSelectedLanguage().common.cancel + (hasElementToDisplay ? ` ${processActivity?.title}` : '');

            cancelPosition.push({
                color: 'danger',
                key: 'cancel',
                label: cancelLabel,
                onClick: handleClickCancel,
                variant: 'outline',
            });
        }
        if (canCopyReadSectionsToClipboard()) {
            secondaryActionItems.push({
                key: 'copy',
                label: getSelectedLanguage().common.copy,
                onClick: handleClickCopyReadSectionsToClipboard,
            });
        }
        if (canPrint()) {
            secondaryActionItems.push({
                key: 'print',
                label: getSelectedLanguage().request.statusAction_printReferral,
                onClick: handleClickPrint,
            });
        }

        return {
            primaryActionItems: primaryActionItems.length ? primaryActionItems : undefined,
            secondaryActionItems: secondaryActionItems.length ? secondaryActionItems : undefined,
        };
    }

    function handleClickCancel(): void {
        setShowCancelModal(true);
    }

    async function handleClickCopyReadSectionsToClipboard(): Promise<void> {
        let text = '';
        for (const section of readSections) {
            text = addTextBlockWithEachItemOnOneLine(
                text,
                section.sectionTitle ?? null,
                section.items.filter((item) => item.key !== 'assets')
            );
        }
        await navigator.clipboard.writeText(text);
        showToast(ToastType.success, getSelectedLanguage().toast.copyTextSuccess);
    }

    function handleClickEdit(): void {
        if (processAction) {
            setActiveProcessAction(processAction);
        }
    }

    async function handleClickPrint() {
        if (processActivity) {
            downloadPdf(processActivity.id);
        }
    }

    async function handleClickModify(): Promise<void> {
        setActiveModifyActivity(processActivity);
    }

    async function handleConfirmRemove(): Promise<void> {
        setShowRemoveModal(false);

        if (processAction) {
            await deleteProcessActionDraft(processAction);

            let message: string;
            if (serviceActivityType === ServiceActivityType.unknown) {
                message = getSelectedLanguage().toast.removeSuccessDefault;
            } else {
                message = formatString(getSelectedLanguage().toast.removeSuccess, [
                    getSelectedLanguage().serviceActivityType[serviceActivityType],
                ]);
            }

            showToast(ToastType.info, message);
        }
    }

    function transformReadSections(): IProcessActivityReadSection[] {
        return registryEntry?.readSectionsTransformer
            ? registryEntry.readSectionsTransformer(processActivity.readSections)
            : processActivity.readSections;
    }
}
